/**
 * Curated Canadian city labels (Stage 5 §5.7).
 *
 * A controlled, tiered label set rendered with a Cesium LabelCollection — we do
 * NOT use Bing/Mapbox auto-labels, so typography density is deterministic and
 * the country view stays readable. Each tier has its own distanceDisplayCondition
 * so labels reveal progressively as the operator zooms:
 *
 *   Tier 1 — visible at country zoom (anchor metros + every prov/terr capital)
 *   Tier 2 — regional zoom: non-capital cities >100K
 *   Tier 3 — street/city zoom: cities ~20–100K
 *   Tier 4 — street/city zoom: fire-prone smaller communities (wildfire COP)
 *
 * Coordinates are city-centre lon/lat to ~3 dp (a few hundred metres — ample
 * for a label anchor).
 */
import * as Cesium from 'cesium';
import { COUNTRY_VISIBLE_FAR_M, REGIONAL_VISIBLE_FAR_M } from '../coneVisibility';

export type CityTier = 1 | 2 | 3 | 4;

export interface CityLabel {
  name: string;
  lat: number;
  lon: number;
  tier: CityTier;
}

/** Street/city far bound (m) for tiers 3 & 4 — labels appear only once the eye
 *  is close in, so smaller communities don't clutter the regional view. */
export const CITY_STREET_FAR_M = 150_000;

/**
 * The `far` distance (m) for a tier's distanceDisplayCondition. Bigger ⇒ shown
 * from higher altitude. Tier 1 is effectively always-on; tier 2 blooms at
 * regional; tiers 3/4 only at street/city. Reuses the same country/regional
 * thresholds as the camera visibility tiers so labels and markers reveal in
 * lockstep (see coneVisibility.ts §5.5).
 */
export function cityLabelFar(tier: CityTier): number {
  switch (tier) {
    case 1:
      return COUNTRY_VISIBLE_FAR_M;
    case 2:
      return REGIONAL_VISIBLE_FAR_M;
    case 3:
    case 4:
      return CITY_STREET_FAR_M;
  }
}

export const CANADIAN_CITIES: ReadonlyArray<CityLabel> = [
  // Tier 1 — country zoom. The anchor metros + the national capital + EVERY
  // provincial & territorial capital (founder decision 2026-06-07: every
  // province's capital anchors the country view). 13 capitals + 4 non-capital
  // anchor metros = 17 labels at country zoom.
  // -- anchor metros + national capital --
  { name: 'Toronto', lat: 43.651, lon: -79.383, tier: 1 }, // ON capital + metro
  { name: 'Montréal', lat: 45.502, lon: -73.567, tier: 1 },
  { name: 'Vancouver', lat: 49.283, lon: -123.121, tier: 1 },
  { name: 'Calgary', lat: 51.045, lon: -114.057, tier: 1 },
  { name: 'Ottawa', lat: 45.421, lon: -75.697, tier: 1 }, // national capital
  // -- provincial capitals --
  { name: 'Edmonton', lat: 53.546, lon: -113.494, tier: 1 }, // AB (also a metro)
  { name: 'Victoria', lat: 48.428, lon: -123.366, tier: 1 }, // BC
  { name: 'Regina', lat: 50.445, lon: -104.619, tier: 1 }, // SK
  { name: 'Winnipeg', lat: 49.895, lon: -97.138, tier: 1 }, // MB
  { name: 'Québec City', lat: 46.813, lon: -71.208, tier: 1 }, // QC
  { name: 'Fredericton', lat: 45.964, lon: -66.643, tier: 1 }, // NB
  { name: 'Halifax', lat: 44.649, lon: -63.576, tier: 1 }, // NS
  { name: 'Charlottetown', lat: 46.238, lon: -63.131, tier: 1 }, // PE
  { name: "St. John's", lat: 47.561, lon: -52.713, tier: 1 }, // NL
  // -- territorial capitals --
  { name: 'Whitehorse', lat: 60.721, lon: -135.057, tier: 1 }, // YT
  { name: 'Yellowknife', lat: 62.454, lon: -114.372, tier: 1 }, // NT
  { name: 'Iqaluit', lat: 63.747, lon: -68.517, tier: 1 }, // NU

  // Tier 2 — regional zoom (non-capital cities >100K).
  { name: 'Hamilton', lat: 43.256, lon: -79.871, tier: 2 },
  { name: 'London', lat: 42.984, lon: -81.245, tier: 2 },
  { name: 'Kitchener–Waterloo', lat: 43.451, lon: -80.493, tier: 2 },
  { name: 'Mississauga', lat: 43.589, lon: -79.644, tier: 2 },
  { name: 'Brampton', lat: 43.731, lon: -79.762, tier: 2 },
  { name: 'Surrey', lat: 49.105, lon: -122.825, tier: 2 },
  { name: 'Saskatoon', lat: 52.157, lon: -106.671, tier: 2 },

  // Tier 3 — street/city zoom (cities ~20–100K).
  { name: 'Barrie', lat: 44.389, lon: -79.69, tier: 3 },
  { name: 'Guelph', lat: 43.545, lon: -80.248, tier: 3 },
  { name: 'Kingston', lat: 44.231, lon: -76.486, tier: 3 },
  { name: 'Greater Sudbury', lat: 46.492, lon: -80.993, tier: 3 },
  { name: 'Thunder Bay', lat: 48.38, lon: -89.247, tier: 3 },
  { name: 'Windsor', lat: 42.317, lon: -83.036, tier: 3 },
  { name: 'Oshawa', lat: 43.897, lon: -78.866, tier: 3 },
  { name: 'Sherbrooke', lat: 45.404, lon: -71.889, tier: 3 },
  { name: 'Trois-Rivières', lat: 46.343, lon: -72.543, tier: 3 },
  { name: 'Saguenay', lat: 48.428, lon: -71.069, tier: 3 },
  { name: 'Gatineau', lat: 45.477, lon: -75.701, tier: 3 },
  { name: 'Moncton', lat: 46.09, lon: -64.79, tier: 3 },
  { name: 'Saint John', lat: 45.273, lon: -66.063, tier: 3 },
  { name: 'Sydney', lat: 46.137, lon: -60.197, tier: 3 },
  { name: 'Red Deer', lat: 52.268, lon: -113.811, tier: 3 },
  { name: 'Lethbridge', lat: 49.694, lon: -112.833, tier: 3 },
  { name: 'Medicine Hat', lat: 50.041, lon: -110.677, tier: 3 },
  { name: 'Grande Prairie', lat: 55.171, lon: -118.795, tier: 3 },
  { name: 'Nanaimo', lat: 49.166, lon: -123.94, tier: 3 },
  { name: 'Prince George', lat: 53.917, lon: -122.749, tier: 3 },
  { name: 'Abbotsford', lat: 49.05, lon: -122.328, tier: 3 },
  { name: 'Brandon', lat: 49.848, lon: -99.95, tier: 3 },
  { name: 'Sault Ste. Marie', lat: 46.521, lon: -84.347, tier: 3 },
  { name: 'North Bay', lat: 46.309, lon: -79.46, tier: 3 },
  { name: 'Belleville', lat: 44.163, lon: -77.383, tier: 3 },
  { name: 'Peterborough', lat: 44.3, lon: -78.319, tier: 3 },
  { name: 'Sarnia', lat: 42.974, lon: -82.405, tier: 3 },
  { name: 'Cornwall', lat: 45.021, lon: -74.73, tier: 3 },
  { name: 'Drummondville', lat: 45.883, lon: -72.484, tier: 3 },

  // Tier 4 — street/city zoom (fire-prone smaller communities).
  { name: 'Fort McMurray', lat: 56.726, lon: -111.38, tier: 4 },
  { name: 'Wood Buffalo', lat: 58.5, lon: -111.5, tier: 4 },
  { name: 'Slave Lake', lat: 55.283, lon: -114.769, tier: 4 },
  { name: 'Hinton', lat: 53.412, lon: -117.563, tier: 4 },
  { name: 'Whitecourt', lat: 54.143, lon: -115.686, tier: 4 },
  { name: 'Kelowna', lat: 49.888, lon: -119.496, tier: 4 },
  { name: 'West Kelowna', lat: 49.863, lon: -119.583, tier: 4 },
  { name: 'Penticton', lat: 49.491, lon: -119.586, tier: 4 },
  { name: 'Kamloops', lat: 50.674, lon: -120.328, tier: 4 },
  { name: 'Cranbrook', lat: 49.512, lon: -115.769, tier: 4 },
  { name: 'Williams Lake', lat: 52.142, lon: -122.142, tier: 4 },
  { name: 'Vernon', lat: 50.267, lon: -119.272, tier: 4 },
  { name: 'Salmon Arm', lat: 50.7, lon: -119.284, tier: 4 },
  { name: 'Lytton', lat: 50.231, lon: -121.582, tier: 4 },
  { name: 'Logan Lake', lat: 50.494, lon: -120.808, tier: 4 },
];

export interface CityLabelsHandle {
  destroy(): void;
}

/**
 * Render the curated city labels into `viewer` via a single LabelCollection.
 * White text with a thin dark outline (the "1px shadow" for contrast against
 * imagery), screen-space, depth-test disabled so labels never hide behind
 * terrain. Returns a handle whose `destroy()` removes the collection.
 *
 * Labels carry no entity id, so a click on one is a no-op selection-wise (the
 * MapView pick handler sees no selectable id) — they are pure context.
 */
export function installCityLabels(viewer: Cesium.Viewer): CityLabelsHandle {
  const collection = viewer.scene.primitives.add(new Cesium.LabelCollection());
  const outline = Cesium.Color.fromCssColorString('#0b1020');
  for (const c of CANADIAN_CITIES) {
    collection.add({
      position: Cesium.Cartesian3.fromDegrees(c.lon, c.lat),
      text: c.name,
      font: '600 13px "Inter", system-ui, -apple-system, sans-serif',
      fillColor: Cesium.Color.WHITE,
      outlineColor: outline,
      outlineWidth: 2,
      style: Cesium.LabelStyle.FILL_AND_OUTLINE,
      horizontalOrigin: Cesium.HorizontalOrigin.LEFT,
      verticalOrigin: Cesium.VerticalOrigin.CENTER,
      pixelOffset: new Cesium.Cartesian2(6, 0),
      disableDepthTestDistance: Number.POSITIVE_INFINITY,
      distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, cityLabelFar(c.tier)),
    });
  }
  return {
    destroy(): void {
      viewer.scene.primitives.remove(collection);
    },
  };
}
